import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send, Inbox } from "lucide-react";

/** Correspondence reply-inbox for member-contact and contact-us threads. */
export default function CmsCorrespondence() {
  const [threads, setThreads] = useState([]);
  const [counts, setCounts] = useState({ total: 0 });
  const [selected, setSelected] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await http.get("/admin/cms/correspondence");
      setThreads(r.data?.threads || []);
      setCounts(r.data?.counts || { total: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load correspondence");
    }
  };
  useEffect(() => { load(); }, []);

  const reply = async () => {
    if (!selected || !body.trim()) return;
    setBusy(true);
    try {
      await http.post("/admin/cms/correspondence/reply", {
        subject: subject || undefined,
        body: body.trim(),
        thread_id: selected.id,
        thread_source: selected._source,
        to_email: selected.email || selected.contact_email,
        to_user_id: selected.user_id,
      });
      toast.success("Reply sent");
      setBody("");
      setSubject("");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reply failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl" data-testid="cms-correspondence">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <h1 className="text-2xl font-semibold text-slate-900">Correspondence</h1>
      <p className="mt-1 text-sm text-slate-500">
        Reply to user contact-us threads and family-tree contact requests. {counts.total} thread{counts.total === 1 ? "" : "s"} loaded.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_420px]">
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {threads.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <Inbox className="mx-auto mb-2 h-6 w-6 text-slate-300" />
              No open threads — everything looks caught up.
            </div>
          ) : (
            <ul>
              {threads.map((t) => (
                <li key={`${t._source}-${t.id}`}>
                  <button
                    onClick={() => setSelected(t)}
                    data-testid={`cms-corr-thread-${t.id}`}
                    className={`flex w-full items-start justify-between gap-3 border-b border-slate-100 px-4 py-3 text-left last:border-b-0 hover:bg-slate-50 ${selected?.id === t.id ? "bg-emerald-50/60" : ""}`}
                  >
                    <div>
                      <div className="text-sm font-medium text-slate-900">
                        {t.from_name || t.name || t.contact_name || t.email || "Anonymous"}
                      </div>
                      <div className="mt-0.5 line-clamp-2 text-xs text-slate-500">
                        {t.message || t.body || t.reason || "(no message)"}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-[11px] text-slate-500">{t._source.replace("_", " ")}</div>
                      <div className="mt-1">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          t.status === "replied" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                        }`}>{t.status || "new"}</span>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <aside className="sticky top-4 h-fit rounded-xl border border-slate-200 bg-white p-4">
          {!selected ? (
            <div className="text-sm text-slate-500">Pick a thread on the left to reply.</div>
          ) : (
            <div data-testid="cms-corr-reply-panel">
              <div className="mb-2 text-xs uppercase tracking-wider text-emerald-700">Reply</div>
              <div className="text-sm font-medium text-slate-900">To: {selected.email || selected.contact_email || selected.from_name || "user"}</div>
              <div className="mt-2 max-h-40 overflow-y-auto rounded-md bg-slate-50 p-2 text-xs text-slate-700">
                {selected.message || selected.body || selected.reason || "(no message)"}
              </div>
              <Input
                className="mt-3"
                placeholder="Subject (optional)"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="cms-corr-subject"
              />
              <Textarea
                rows={6}
                className="mt-2"
                placeholder="Type your reply…"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                data-testid="cms-corr-body"
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
                <Button
                  onClick={reply}
                  disabled={busy || !body.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="cms-corr-send"
                >
                  <Send className="mr-1 h-4 w-4" /> {busy ? "Sending…" : "Send reply"}
                </Button>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
